import React from 'react';
import { css } from '@compiled/react';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { useNavigate } from 'react-router-dom';
import { token } from '@atlaskit/tokens';
import { Heading, Text } from '@atlassian/learning-components';
import { media } from '@atlaskit/primitives/responsive';

import { AllLearningPathsSection } from 'src/pages/HomePage/children/AllLearningPathsSection';
import { HeroHeader } from 'src/components/HeroHeader';
import { ENABLEMENT_ACADEMY_IN_ALP_FF } from 'src/constants';

import { ContentSection } from './children/ContentSection';

export const HIGHLIGHTED_CATALOGS_DATA = [
  {
    title: 'Get Accredited',
    assetURL: '/images/homePage/getStartedQuickly.png',
    altText: 'Get started quickly image',
    url: '/enable/hub/get-accredited',
  },
  {
    title: 'Maintain Your Accreditation',
    assetURL: '/images/homePage/boostProductivity.png',
    altText: 'Boost productivity and teamwork image',
    url: '/enable/hub/maintain-your-accreditation',
  },
];

export const EnablementHomePage: React.FC = () => {
  const navigate = useNavigate();
  const adobeAccountId = 'mock-account-id';

  const enablementAcademyEnabled = FeatureGates.checkGate(ENABLEMENT_ACADEMY_IN_ALP_FF);

  if (!enablementAcademyEnabled) {
    navigate('/');

    return null;
  }

  return (
    <div css={homePageContainerStyles}>
      <HeroHeader
        logoText="Partner Enablement Academy"
        heading="Grow skills to boost your success"
        subtext="Earn and maintain accreditations, strengthen your technical and sales confidence, and take your Atlassian practice to the next level."
        backgroundImagePath="/images/enablement/partnerHero.png"
      />
      <section key="enablement-main-section" css={sectionStyles}>
        <div css={headingContainerStyles}>
          <Heading level={2}>Showcase skills. Build credibility</Heading>
        </div>
        <Text size="large">
          Accreditation proves your expertise as an Atlassian Solution Partner, builds customer trust, and helps you
          stand out, unlocking new opportunities.
        </Text>
      </section>
      <AllLearningPathsSection
        cardsData={HIGHLIGHTED_CATALOGS_DATA}
        gridColumns={2}
        backgroundColor="#FFFFFF"
        showHeader={false}
      />
      <div css={sectionWrapperStyles}>
        <div css={contentSectionStyles}>
          <ContentSection title="New Training" adobeAccountId={adobeAccountId} />
        </div>
      </div>
      <div css={sectionWrapperStyles}>
        <div css={contentSectionStyles}>
          <ContentSection title="Products & Solutions" adobeAccountId={adobeAccountId} />
        </div>
      </div>
      <div css={sectionWrapperStyles}>
        <div css={contentSectionStyles}>
          <ContentSection title="Role Based Training" adobeAccountId={adobeAccountId} />
        </div>
      </div>
    </div>
  );
};

const sectionStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '16px',

  maxWidth: '810px',
  width: '100%',
  margin: '0 auto',
  textAlign: 'center',

  [media.below.md]: {
    maxWidth: '720px',
  },

  [media.below.sm]: {
    maxWidth: '343px',
  },
});

const headingContainerStyles = css({
  marginTop: token('space.400'),
  marginBottom: token('space.200'),
});

const homePageContainerStyles = css({
  width: '100%',

  '@media (min-width: 1280px)': {
    marginTop: '68px',
  },

  '@media (max-width: 1279px)': {
    marginTop: '0',
  },
});

const contentSectionStyles = css({
  maxWidth: '1022px',
  width: '100%',
  margin: '0 auto',
  padding: '0 24px',
});

const sectionWrapperStyles = css({
  width: '100%',
  padding: '40px 0',

  '&:nth-child(odd)': {
    backgroundColor: '#FFFFFF',
  },

  '&:nth-child(even)': {
    backgroundColor: '#F8F8F8',
  },
});
